package com.devi3east.bashbuddy.dashboard.data.repositories

import com.devi3east.bashbuddy.dashboard.data.api.DashboardApi
import com.devi3east.bashbuddy.dashboard.domain.DashboardRepository
import com.devi3east.bashbuddy.dashboard.responses.DashboardResponse
import com.devi3east.bashbuddy.dashboard.responses.PrizeListResponse
import com.devi3east.bashbuddy.dashboard.results.DeleteResult
import com.devi3east.bashbuddy.features.events.requests.CreateDraftTicketRequest
import com.devi3east.bashbuddy.features.events.requests.UpdateDraftEventRequest
import com.devi3east.bashbuddy.features.events.requests.UpdateTicketRequest
import com.devi3east.bashbuddy.prizeList.requests.CreatePrizeRequest
import com.devi3east.bashbuddy.prizeList.requests.CreatePrizeSeriesRequest
import com.devi3east.bashbuddy.prizeList.requests.UpdatePrizeRequest
import com.devi3east.bashbuddy.purchase.domain.models.Allocation
import com.devi3east.bashbuddy.supabase.SupabaseClient
import io.github.jan.supabase.storage.storage
import io.ktor.client.plugins.ClientRequestException

class DashboardRepositoryImpl(
    private val api: DashboardApi
) : DashboardRepository {

    override suspend fun getDashboard(token: String): Result<DashboardResponse> {
        return try {
            val response = api.getDashboard(token)
            Result.success(response)
        } catch (e: ClientRequestException) {
            Result.failure(Exception("Client error: ${e.response.status}"))
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun createDraftEvent(token: String): String? {
        return try {
            val eventId = api.createDraftEvent(token)
            eventId
        } catch (e: Exception) {
            null
        }
    }

    override suspend fun updateDraftEvent(token: String, eventId: String, request: UpdateDraftEventRequest): Boolean {
        return try {
            val success = api.updateEvent(token, eventId, request)
            success
        } catch (e: Exception) {
            false
        }
    }

    override suspend fun createDraftTicket(token: String, eventId: String, request: CreateDraftTicketRequest): String? {
        return try {
            val ticketId = api.createDraftTicket(token, eventId, request)
            ticketId
        } catch (e: Exception) {
            null
        }
    }

    override suspend fun updateTicket(token: String, ticketId: String, request: UpdateTicketRequest): Boolean {
        return try {
            val success = api.updateTicket(token, ticketId, request)
            success
        } catch (e: Exception) {
            false
        }
    }

    override suspend fun deleteEvent(token: String, eventId: String): DeleteResult {
        return try {
            val result = api.deleteEvent(token, eventId)
            result
        } catch (e: Exception) {
            DeleteResult(false, e.message ?: "Unexpected error")
        }
    }

    override suspend fun deleteTicket(token: String, ticketId: String): DeleteResult {
        return try {
            val result = api.deleteTicket(token, ticketId)
            result
        } catch (e: Exception) {
            DeleteResult(false, e.message ?: "Unexpected error")
        }
    }

    // 🔹 New functions
    override suspend fun adjustPrintedTicketStock(token: String, ticketId: String, newMaxQuantity: Int): Boolean {
        return try {
            val success = api.adjustPrintedTicketStock(token, ticketId, newMaxQuantity)
            success
        } catch (e: Exception) {
            false
        }
    }

    override suspend fun getAllocationsForTicket(token: String, ticketId: String): Result<List<Allocation>> {
        return try {
            val allocations = api.getAllocationsForTicket(token, ticketId)
            Result.success(allocations)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun updateAllocationChanceNumbers(token: String, ticketId: String, updates: Map<String, List<String>>): Boolean {
        return try {
            val success = api.updateAllocationChanceNumbers(token, ticketId, updates)
            success
        } catch (e: Exception) {
            false
        }
    }

    override suspend fun publishEvent(token: String, eventId: String): Boolean {
        return try {
            val success = api.publishEvent(token, eventId)
            success
        } catch (e: Exception) {
            false
        }
    }

    override suspend fun getAllocationsForTicketDetail(token: String, ticketId: String): Result<List<Allocation>> {
        return try {
            val allocations = api.getAllocationsForTicketDetail(token, ticketId)
            Result.success(allocations)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun createPrize(
        token: String,
        eventId: String,
        request: CreatePrizeRequest
    ): String? {
        return try {
            val prizeId = api.createPrize(token, eventId, request)
            prizeId
        } catch (e: Exception) {
            null
        }
    }

    override suspend fun createPrizeSeries(
        token: String,
        eventId: String,
        request: CreatePrizeSeriesRequest
    ): List<String>? {
        return try {
            val prizeIds = api.createPrizeSeries(token, eventId, request)
            prizeIds
        } catch (e: Exception) {
            null
        }
    }

    override suspend fun updatePrize(
        token: String,
        prizeId: String,
        request: UpdatePrizeRequest
    ): Boolean {
        return try {
            val success = api.updatePrize(token, prizeId, request)
            success
        } catch (e: Exception) {
            false
        }
    }

    override suspend fun deletePrize(token: String, prizeId: String): DeleteResult {
        return try {
            val result = api.deletePrize(token, prizeId)
            result
        } catch (e: Exception) {
            DeleteResult(false, e.message ?: "Unexpected error")
        }
    }

    override suspend fun getPrizesByEventId(eventId: String): Result<PrizeListResponse> = try {
        val response = api.getPrizesByEventId(eventId)
        Result.success(response)
    } catch (e: Exception) {
        Result.failure(e)
    }

    override suspend fun pauseTicket(token: String, ticketId: String, paused: Boolean): Boolean {
        return api.pauseTicket(token, ticketId, paused)
    }

    override suspend fun deleteImageFromSupabase(publicUrl: String) {
        try {
            val parts = publicUrl.split("/storage/v1/object/public/")
            if (parts.size != 2) {
                return
            }

            val bucketAndPath = parts[1]
            val firstSlash = bucketAndPath.indexOf("/")
            if (firstSlash == -1) {
                return
            }

            val bucketName = bucketAndPath.substring(0, firstSlash)
            val path = bucketAndPath.substring(firstSlash + 1)

            SupabaseClient.client.storage
                .from(bucketName)
                .delete(listOf(path))

        } catch (_: Exception) {
            // intentionally empty
        }
    }

}